import React, { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { AppSpin } from '@/components/atoms';
import PatientPageHeader from '../components/PatientPageHeader';
import PatientCheckinMailForm from '../components/PatientCheckinMailForm';
import PatientCheckinMailFormBottomBar from '../components/PatientCheckinMailFormBottomBar';
import { useMainPatient } from '@/hooks/main/useMainPatient';
import {
  useCheckinMailDelete,
  useCheckinMailOne,
  useCheckinMailUpdate,
} from '@/hooks/main/useCheckinMailEdit';
import useAppToast from '@/hooks/useAppToast';
import useAppModal from '@/hooks/useAppModal';
import { authorizeNavigation } from '@/hooks/useFormConfirmationBlocker';
import { getApiErrorMessage } from '@/utils/apiError';
import { TENANT_ROUTES } from '@/constants/routes';
import type { CheckinMailFormSchema } from '@/schemas/checkinMailSchema';
import type { CheckinMailPayload } from '@/services/main/checkInMain/checkinMailService';
import { NotFound } from '@/pages/main/system/NotFound';
import { DELETE_CONFIRM_DATA_MESSAGE } from '@/constants/messages';

const PatientCheckinMailEditPage: React.FC = () => {
  const { id, mailId, tenant } = useParams<{ tenant?: string; id?: string; mailId?: string }>();
  const navigate = useNavigate();
  const toast = useAppToast();
  const { confirm } = useAppModal();

  const patientId = id ? parseInt(id, 10) : null;
  const patientIdStr = id ?? '';
  const mailIdNum = mailId ? parseInt(mailId, 10) : null;

  const { data: patient, isLoading: patientLoading } = useMainPatient(patientId ?? undefined);
  const {
    data: record,
    isLoading: detailLoading,
    isError: detailError,
  } = useCheckinMailOne(patientIdStr, mailId);
  const updateMutation = useCheckinMailUpdate(patientIdStr);
  const deleteMutation = useCheckinMailDelete(patientIdStr);

  const listUrl = TENANT_ROUTES.CHECKIN_MAILS.LIST(tenant ?? '', patientIdStr);

  const handleBack = useCallback(() => {
    navigate(listUrl);
  }, [navigate, listUrl]);

  const handleSubmit = useCallback(
    async (values: CheckinMailFormSchema) => {
      if (!mailIdNum) return;
      const payload: CheckinMailPayload = {
        email: values.email,
        is_status: !!values.is_status,
        add_sentence: values.add_sentence ?? '',
        remarks: values.remarks ?? '',
      };
      try {
        await updateMutation.mutateAsync({ id: mailIdNum, payload });
        toast.success('更新が正常に終了しました。');
        authorizeNavigation();
        navigate(listUrl);
      } catch (err) {
        toast.error(getApiErrorMessage(err, '更新ができませんでした。'));
      }
    },
    [mailIdNum, updateMutation, toast, navigate, listUrl]
  );

  const handleDelete = useCallback(() => {
    if (!mailIdNum) return;
    confirm({
      title: DELETE_CONFIRM_DATA_MESSAGE,
      okText: 'OK',
      cancelText: 'キャンセル',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteMutation.mutateAsync(mailIdNum);
          authorizeNavigation();
          navigate(listUrl, { replace: true });
        } catch {
          // toast handled in hook
        }
      },
    });
  }, [mailIdNum, confirm, deleteMutation, navigate, listUrl]);

  const defaultValues = useMemo<Partial<CheckinMailFormSchema> | undefined>(() => {
    if (!record) return undefined;
    return {
      email: record.email ?? '',
      is_status: !!record.is_status,
      add_sentence: record.add_sentence ?? '',
      remarks: record.remarks ?? '',
    };
  }, [record]);

  if (patientId === null || isNaN(patientId) || mailIdNum === null || isNaN(mailIdNum)) {
    return <NotFound />;
  }

  if ((patientLoading && !patient) || detailLoading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <AppSpin size="large" />
      </div>
    );
  }

  if (!patient || detailError || !record) {
    return <NotFound />;
  }

  const formId = 'patient-checkin-mail-edit-form';
  const patientName = patient?.current_patient_information?.name ?? '';
  const headerLabel = patient?.serial_number
    ? `${patient.serial_number}：${patientName || '—'}`
    : patientIdStr;
  const isBusy = updateMutation.isPending || deleteMutation.isPending;

  return (
    <div className="patient-edit-page pb-24">
      <PatientPageHeader
        label={headerLabel}
        patientId={patientIdStr}
        tenant={tenant}
        navigate={path => navigate(path)}
        activeKey="checkin-mails"
        disabled={isBusy}
      />

      <div>
        <PatientCheckinMailForm
          key={mailIdNum}
          formId={formId}
          mode="edit"
          patientName={patientName}
          disabled={isBusy}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
        />
      </div>

      <PatientCheckinMailFormBottomBar
        mode="edit"
        isSubmitting={updateMutation.isPending}
        formId={formId}
        onBack={handleBack}
        onDelete={handleDelete}
        isDeleting={deleteMutation.isPending}
      />
    </div>
  );
};

export default PatientCheckinMailEditPage;
